/**
 * RNNoise Realtime — Real-time AI noise suppression for microphone input.
 * Creates a ScriptProcessorNode that processes audio through RNNoise WASM.
 * RNNoise works at 48kHz with 480-sample frames (10ms).
 *
 * Two flavours of node behind ONE door:
 *   warmWorklet(ctx)             loads the AudioWorklet processor
 *                                (public/js/rnnoise-worklet.js) and the wasm
 *                                bytes once per context — call it as soon as
 *                                the context exists;
 *   createNode(ctx)              the AudioWorklet node (959 frames ≈ 20 ms,
 *                                audio thread, no background-tab throttling)
 *                                when the context is warm, else the
 *                                ScriptProcessor(4096) (9151 frames ≈ 190 ms,
 *                                main thread) — the legacy path;
 *   createWorkletNode(ctx)       warm + create in one await, null on failure.
 * nodeLatencyFrames(node) tells a recorder exactly how much to subtract.
 *
 * Usage:
 *   await RNNoiseRealtime.init();
 *   var node = RNNoiseRealtime.createNode(audioContext);
 *   micSource.connect(node);
 *   node.connect(destination);
 *   // ... later:
 *   RNNoiseRealtime.destroyNode(node);
 */
window.RNNoiseRealtime = (function() {
    'use strict';

    var _module = null;
    var _loading = false;
    var _ready = false;
    var FRAME = 480;       // RNNoise frame size (10ms at 48kHz)
    var TARGET_SR = 48000;
    var BUFFER_SIZE = 4096; // ScriptProcessor buffer

    // Per-node state storage
    var _nodes = new Map();
    var _wasmBytes = null;       // Promise<ArrayBuffer> of the vendored rnnoise.wasm (worklet path)
    var _wasmBytesReady = null;  // the ArrayBuffer itself once fetched (sync createNode needs it in hand)

    /* Не-конечный сэмпл на входе шумодава (NaN, ±Inf с устройства). Оба вида
     * узла заменяют его нулём ДО модели: один такой сэмпл отравляет состояние
     * RNNoise до конца записи, а биквады ниже по цепочке после него не
     * восстанавливаются (TIMBRICA-FRONTEND-MH). Отчёт — один на страницу. */
    var _toldNonFinite = false;
    function reportNonFinite(n, lane) {
        if (_toldNonFinite) return;
        _toldNonFinite = true;
        try { if (window.__ae) window.__ae('feature_use', { action: 'audio_input_nonfinite', where: 'rnnoise', n: n, lane: lane }); } catch(_){}
    }

    /** The AudioWorklet node — only after warmWorklet(ctx) resolved true. */
    function buildWorkletNode(ctx) {
        var node = new AudioWorkletNode(ctx, 'rnnoise-realtime', {
            numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1],
            processorOptions: { wasm: _wasmBytesReady.slice(0) }      // the worklet gets its own copy
        });
        var readyResolve;
        var ready = new Promise(function(res) { readyResolve = res; });
        node.port.onmessage = function(ev) {
            var d = ev.data || {};
            if ('ready' in d) {
                readyResolve(!!d.ready);
                if (!d.ready) {
                    try { if (window.__ae) window.__ae('feature_use', { action: 'rnnoise_worklet_failed', reason: String(d.error || '').slice(0, 80) }); } catch(_){}
                }
            }
            if (d.nonFinite) reportNonFinite(d.nonFinite, 'worklet');
        };
        _nodes.set(node, { worklet: true, ready: ready, kill: function() { try { node.port.postMessage({ cmd: 'stop' }); } catch(e) {} } });
        return node;
    }

    return {
        /**
         * Initialize RNNoise WASM module (idempotent, non-blocking)
         * @returns {Promise<boolean>}
         */
        init: function() {
            if (_ready) return Promise.resolve(true);
            if (_loading) {
                return new Promise(function(resolve) {
                    var check = setInterval(function() {
                        if (!_loading) { clearInterval(check); resolve(_ready); }
                    }, 50);
                });
            }
            _loading = true;

            return new Promise(function(resolve) {
                // Pre-flight: WebAssembly missing (Mobile Safari iOS in
                // restricted contexts, very old browsers). The vendored
                // /vendor/rnnoise/rnnoise.js calls `abort('no native wasm
                // support detected')` synchronously inside the emscripten
                // module factory, which races our Promise.catch and
                // surfaces as Sentry 117186490. Bail early so the vendor
                // factory never runs.
                if (typeof WebAssembly === 'undefined') {
                    try {
                        if (window.__ae) window.__ae('feature_use', { action: 'rnnoise_unsupported', reason: 'wasm_unavailable' });
                    } catch(_){}
                    _loading = false;
                    resolve(false);
                    return;
                }
                if (typeof createRNNWasmModule === 'undefined') {
                    console.warn('RNNoiseRealtime: createRNNWasmModule not found');
                    _loading = false;
                    resolve(false);
                    return;
                }
                createRNNWasmModule({
                    locateFile: function(path) { return '/vendor/rnnoise/' + path; }
                }).then(function(mod) {
                    _module = mod;
                    _ready = true;
                    _loading = false;
                    resolve(true);
                }).catch(function(e) {
                    console.error('RNNoiseRealtime init failed:', e);
                    _loading = false;
                    resolve(false);
                });
            });
        },

        isReady: function() { return _ready; },

        /**
         * Fixed processing latency of a node made here, in frames — what a
         * recorder subtracts so the take lands where it was sung.
         *   worklet         : 959  = 479 (frame accumulation) + 480 inside RNNoise
         *                            (overlap-add: it answers for the previous frame)
         *   ScriptProcessor : 9151 = 2 × 4096 (input fill + output turn) + 959
         * Both verified with a graph-scheduled click through the node
         * (tests/playwright/rnnoise-worklet-matches-the-scriptprocessor.mjs).
         * /vocal placed every take ~190 ms late because of the second line.
         */
        nodeLatencyFrames: function(node) {
            if (!node) return 0;
            var info = _nodes.get(node);
            if (info && info.worklet) return 2 * FRAME - 1;
            return BUFFER_SIZE * 2 + 2 * FRAME - 1;
        },

        /**
         * Make the worklet flavour available on this context — SYNCHRONOUSLY
         * from then on: once this resolves true, createNode(ctx) hands out
         * AudioWorklet nodes (959 frames) instead of ScriptProcessors (9151).
         * Call it the moment the context exists (ensureAudioCtx, initMic) — a
         * warm-up that lands before the first take costs nothing, and a
         * consumer that cannot await (a WebRTC ontrack handler) still gets the
         * worklet if the context was warmed earlier. The module URL is
         * published by components/voice-enhance-toggle
         * (`window.__RNNOISE_WORKLET_URL`, versioned through the asset router).
         * @param {AudioContext} ctx
         * @param {{moduleUrl?:string}} [opts]
         * @returns {Promise<boolean>}  true = createNode(ctx) is now the worklet
         */
        warmWorklet: function(ctx, opts) {
            var url = (opts && opts.moduleUrl) || window.__RNNOISE_WORKLET_URL;
            if (!url || !ctx || !ctx.audioWorklet || typeof AudioWorkletNode === 'undefined') return Promise.resolve(false);
            if (ctx.__rnWorkletWarm) return Promise.resolve(true);
            if (ctx.__rnWorkletWarming) return ctx.__rnWorkletWarming;
            if (!_wasmBytes) {
                _wasmBytes = fetch('/vendor/rnnoise/rnnoise.wasm', { cache: 'force-cache' })
                    .then(function(r) { if (!r.ok) throw new Error('rnnoise.wasm ' + r.status); return r.arrayBuffer(); })
                    .catch(function(e) { _wasmBytes = null; throw e; });
            }
            if (!ctx.__rnWorkletModule) {
                ctx.__rnWorkletModule = ctx.audioWorklet.addModule(url).then(function() { return true; }, function(e) { ctx.__rnWorkletModule = null; throw e; });
            }
            ctx.__rnWorkletWarming = Promise.all([_wasmBytes, ctx.__rnWorkletModule]).then(function(r) {
                _wasmBytesReady = r[0];
                ctx.__rnWorkletWarm = true;
                return true;
            }).catch(function(e) {
                ctx.__rnWorkletWarming = null;
                try { if (window.__ae) window.__ae('feature_use', { action: 'rnnoise_worklet_failed', reason: String(e && e.message || e).slice(0, 80) }); } catch(_){}
                return false;
            });
            return ctx.__rnWorkletWarming;
        },

        /**
         * AudioWorklet flavour, explicitly: warm the context, then build the
         * node. Resolves to the node, or null when the worklet cannot be built
         * (no AudioWorklet, module URL missing, wasm fetch failed) — callers
         * then fall back to createNode(), which is the ScriptProcessor in that
         * case.
         * @param {AudioContext} ctx
         * @param {{moduleUrl?:string}} [opts]
         * @returns {Promise<AudioWorkletNode|null>}
         */
        createWorkletNode: function(ctx, opts) {
            var self = this;
            return this.warmWorklet(ctx, opts).then(function(ok) {
                if (!ok) return null;
                try { return buildWorkletNode(ctx); } catch (e) {
                    try { if (window.__ae) window.__ae('feature_use', { action: 'rnnoise_worklet_failed', reason: String(e && e.message || e).slice(0, 80) }); } catch(_){}
                    return null;
                }
            });
        },

        /** true when createNode(ctx) would hand out the worklet flavour. */
        isWorkletWarm: function(ctx) { return !!(ctx && ctx.__rnWorkletWarm && _wasmBytesReady); },

        /** Resolves once a worklet node's model is live (true) or failed (false). */
        whenReady: function(node) {
            var info = _nodes.get(node);
            return info && info.ready ? info.ready : Promise.resolve(!!info);
        },

        /**
         * Create a real-time RNNoise processing node — the best one this
         * context can give RIGHT NOW: the AudioWorklet flavour (959 frames,
         * audio thread) when warmWorklet(ctx) has resolved, the ScriptProcessor
         * (9151 frames, main thread) otherwise. Mono in → mono out. Insert
         * between mic source and downstream nodes; nodeLatencyFrames(node)
         * says which one you got.
         * @param {AudioContext} ctx
         * @returns {AudioWorkletNode|ScriptProcessorNode}
         */
        createNode: function(ctx) {
            if (ctx && ctx.__rnWorkletWarm && _wasmBytesReady) {
                try { return buildWorkletNode(ctx); } catch (e) {
                    try { if (window.__ae) window.__ae('feature_use', { action: 'rnnoise_worklet_failed', reason: String(e && e.message || e).slice(0, 80) }); } catch(_){}
                }
            }
            if (!_ready || !_module) throw new Error('RNNoiseRealtime not initialized');

            var mod = _module;
            var state = mod._rnnoise_create();
            var inputPtr = mod._malloc(FRAME * 4);
            var outputPtr = mod._malloc(FRAME * 4);

            // Ring buffers for frame accumulation. RNNoise answers for the
            // PREVIOUS frame (overlap-add), so the dry share of the mix is
            // taken from `prevRing` — the same 10 ms as the wet — instead of
            // the frame just accumulated (which summed voice with a 10 ms
            // earlier copy of itself on every voiced frame).
            var inRing = new Float32Array(FRAME);
            var prevRing = new Float32Array(FRAME);
            var inPos = 0;

            // Output FIFO — queue of processed frames
            var outQueue = [];
            var outFrame = null;
            var outPos = 0;

            // Resampling state
            var ratio = ctx.sampleRate / TARGET_SR;
            var needsResample = Math.abs(ctx.sampleRate - TARGET_SR) > 1;

            // VAD-adaptive smoothing
            var smoothVad = 0.5;

            var node = ctx.createScriptProcessor(BUFFER_SIZE, 1, 1);

            /* Флаг живости замыкания. Освобождать память WASM, пока обработчик
             * ещё может выстрелить, нельзя: он ходит по state/inputPtr/outputPtr
             * напрямую, и после _free это ЧУЖАЯ память. Так и падало —
             * «memory access out of bounds» внутри memset у rnnoise_process_frame
             * (Sentry G4, /dictaphone, Android 11). */
            var dead = false;

            node.onaudioprocess = function(e) {
                if (dead) {
                    // Узел уже разобран: отдаём тишину и НЕ трогаем кучу.
                    var silence = e.outputBuffer.getChannelData(0);
                    for (var z = 0; z < silence.length; z++) { silence[z] = 0; }
                    return;
                }
                var input = e.inputBuffer.getChannelData(0);
                var output = e.outputBuffer.getChannelData(0);
                var nonFinite = 0;

                for (var i = 0; i < input.length; i++) {
                    // Resample input sample to 48kHz (or pass through)
                    var sample = input[i];
                    // Same rule as the worklet flavour: NaN/±Inf → 0 before the model.
                    if (sample - sample !== 0) { sample = 0; nonFinite++; }

                    inRing[inPos++] = sample;

                    // When we have a full RNNoise frame, process it
                    if (inPos >= FRAME) {
                        var heap = mod.HEAPF32;
                        var inIdx = inputPtr >> 2;

                        // Copy to WASM memory (scaled to int16 range)
                        for (var j = 0; j < FRAME; j++) {
                            heap[inIdx + j] = inRing[j] * 32768;
                        }

                        // Process frame
                        var vad = mod._rnnoise_process_frame(state, outputPtr, inputPtr);
                        smoothVad = smoothVad * 0.85 + vad * 0.15;

                        // VAD-adaptive strength
                        var strength;
                        if (smoothVad > 0.6) {
                            strength = 0.75; // speech — preserve naturalness
                        } else if (smoothVad < 0.15) {
                            strength = 1.0;  // noise — full suppression
                        } else {
                            var t = (smoothVad - 0.15) / 0.45;
                            strength = 1.0 - t * 0.25;
                        }

                        // Copy processed frame with wet/dry mix
                        var processed = new Float32Array(FRAME);
                        var outIdx = outputPtr >> 2;
                        for (var j = 0; j < FRAME; j++) {
                            var dry = prevRing[j];
                            var wet = heap[outIdx + j] / 32768;
                            processed[j] = wet * strength + dry * (1 - strength);
                        }
                        var swap = prevRing; prevRing = inRing; inRing = swap;

                        outQueue.push(processed);
                        inPos = 0;
                    }

                    // Read from output FIFO
                    if (!outFrame && outQueue.length > 0) {
                        outFrame = outQueue.shift();
                        outPos = 0;
                    }

                    if (outFrame) {
                        output[i] = outFrame[outPos++];
                        if (outPos >= outFrame.length) {
                            outFrame = null;
                            outPos = 0;
                        }
                    } else {
                        output[i] = 0; // silence until first frame processed
                    }
                }
                if (nonFinite) reportNonFinite(nonFinite, 'scriptprocessor');
            };

            _nodes.set(node, {
                state: state, inputPtr: inputPtr, outputPtr: outputPtr,
                kill: function () { dead = true; }
            });
            return node;
        },

        /**
         * Destroy a node and free WASM resources
         * @param {ScriptProcessorNode} node
         */
        destroyNode: function(node) {
            if (!node) return;

            var info = _nodes.get(node);

            /* ⚠️ Порядок здесь — это и есть починка.
             *
             * Раньше стояло `node.disconnect()`, а сразу за ним _free. Но
             * disconnect() у ScriptProcessorNode убирает ИСХОДЯЩИЕ связи —
             * источник остаётся подключённым КО ВХОДУ, и onaudioprocess
             * продолжает вызываться. Обработчик шёл в state/inputPtr/outputPtr,
             * которые к тому моменту уже освобождены, и WASM падал с «memory
             * access out of bounds» внутри memset (Sentry G4).
             *
             * Поэтому сначала обработчик замолкает — И флагом (он гасит вызов,
             * который УЖЕ начался или стоит в очереди), И снятием самого
             * onaudioprocess, — и только потом освобождается память. */
            if (info && info.kill) { try { info.kill(); } catch(e) {} }
            try { node.onaudioprocess = null; } catch(e) {}
            try { node.disconnect(); } catch(e) {}

            if (info && info.worklet) { _nodes.delete(node); return; }   // memory lives in the worklet; 'stop' freed it
            if (info && _module) {
                try {
                    _module._rnnoise_destroy(info.state);
                    _module._free(info.inputPtr);
                    _module._free(info.outputPtr);
                } catch(e) {}
            }
            _nodes.delete(node);
        }
    };
})();
